/**
 * Cross-platform storage abstraction layer
 * 
 * Uses Capacitor Preferences API on native platforms (iOS/Android)
 * and localStorage on web platforms.
 * 
 * This ensures consistent storage behavior across all platforms,
 * particularly fixing the iOS infinite token refresh loop issue
 * where localStorage.clear() doesn't work reliably.
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from './constants';

const isNative = Capacitor.isNativePlatform();

/**
 * Storage abstraction that works on both web and native platforms
 *
 * On iOS/native: Uses Capacitor Preferences as source of truth, but ALSO
 * mirrors to localStorage for synchronous access (isAuthenticated() etc.)
 */
export const storage = {
  /**
   * Get a value from storage
   */
  async get(key: string): Promise<string | null> {
    if (isNative) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },

  /**
   * Set a value in storage
   * On native: writes to BOTH Capacitor Preferences AND localStorage
   * This ensures synchronous checks (like isAuthenticated()) work immediately
   */
  async set(key: string, value: string): Promise<void> {
    if (isNative) {
      // Write to Capacitor Preferences (persistent, reliable)
      await Preferences.set({ key, value });
      // Also write to localStorage for synchronous access
      // This is the key fix for iOS login - isAuthenticated() checks localStorage
      try {
        localStorage.setItem(key, value);
      } catch {
        // localStorage might fail on iOS in some edge cases, but that's ok
        // Capacitor Preferences is the source of truth
      }
    } else {
      localStorage.setItem(key, value);
    }
  },

  /**
   * Remove a value from storage
   */
  async remove(key: string): Promise<void> {
    if (isNative) {
      await Preferences.remove({ key });
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore errors
      }
    } else {
      localStorage.removeItem(key);
    }
  },

  /**
   * Clear all values from storage
   */
  async clear(): Promise<void> {
    if (isNative) {
      await Preferences.clear();
      try {
        localStorage.clear();
      } catch {
        // Ignore errors
      }
    } else {
      localStorage.clear();
    }
  },

  /**
   * Get all keys from storage
   */
  async keys(): Promise<string[]> {
    if (isNative) {
      const { keys } = await Preferences.keys();
      return keys;
    }
    return Object.keys(localStorage);
  },

  /**
   * Initialize storage on app start
   * On native platforms, syncs Capacitor Preferences to localStorage
   * This ensures returning users have their auth data available synchronously
   */
  async initialize(): Promise<void> {
    if (isNative) {
      // Sync specific keys from Capacitor Preferences to localStorage.
      // These MUST match the constants used by auth.ts / api.ts; a mismatch
      // makes returning iOS users appear logged out on cold start despite a
      // valid session persisted in Preferences.
      const keysToSync = [USER_KEY, AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY];
      for (const key of keysToSync) {
        const { value } = await Preferences.get({ key });
        if (value) {
          try {
            localStorage.setItem(key, value);
          } catch {
            // Ignore errors
          }
        }
      }
    }
    // On web, localStorage is already available synchronously
  }
};

/**
 * Synchronous storage wrapper for backward compatibility
 * 
 * WARNING: This uses async operations under the hood on native platforms.
 * Prefer using the async storage API directly for new code.
 * 
 * This is provided for gradual migration of existing code.
 */
export const syncStorage = {
  getItem(key: string): string | null {
    if (isNative) {
      console.warn('syncStorage.getItem() is async on native platforms - use storage.get() instead');
      // Return null and trigger async load in background
      storage.get(key).then(value => {
        // Value will be available on next render
      });
      return null;
    }
    return localStorage.getItem(key);
  },

  setItem(key: string, value: string): void {
    if (isNative) {
      storage.set(key, value).catch(err => {
        console.error('Failed to set storage item:', err);
      });
    } else {
      localStorage.setItem(key, value);
    }
  },

  removeItem(key: string): void {
    if (isNative) {
      storage.remove(key).catch(err => {
        console.error('Failed to remove storage item:', err);
      });
    } else {
      localStorage.removeItem(key);
    }
  },

  clear(): void {
    if (isNative) {
      storage.clear().catch(err => {
        console.error('Failed to clear storage:', err);
      });
    } else {
      localStorage.clear();
    }
  }
};

